/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable quotes */
import { InfoCircleFilled } from "@ant-design/icons";
import {
  Select,
  Switch,
  Table,
  Pagination,
  Tag,
  Space,
  Button,
  Typography,
  DatePicker,
  Row,
  Col
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";

import QueryNotificationsTableModal from "./NotificationsTableModal";
import { showErrorNotification, showSuccessNotification } from "../../../../utils/notification";
import { clamp } from "../../../../utils/responsiveHelpers";
import TooltipWrapper from "../../../common-utils/TooltipWrapper";

const { Text } = Typography;

const DiscordAnalyticsQueriesDashboard = ({ adminConfig, ROOT_URL }) => {
  const BASE_BACKEND_URL = "admin/discord-analytics/queries";
  const currentDate = new Date();
  const twoMonthAgo = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 2,
    currentDate.getDate()
  );

  // Filter configs
  const [batchesList, setBatchesList] = useState([]);
  const [mentorsList, setMentorsList] = useState([]);
  const [studentsList, setStudentsList] = useState([]);
  const [queryStatuses, setQueryStatuses] = useState([]);
  const [excludeInternal, setExcludeInternal] = useState(true);
  const [showUnPingedThankedMentorQueries, setShowUnPingedThankedMentorQueries] = useState(false);
  const [filter, setFilter] = useState({
    excludeInternal: true,
    showUnPingedMentorThankedQueries: false,
    orderByColumnName: "createdAtDesc",
    thankFilterOption: "all",
    startDate: twoMonthAgo,
    endDate: currentDate
  });

  // Query table Data
  const [queriesList, setQueriesList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalQueries, setTotalQueries] = useState(0);

  // Query Notifications
  const [currentQueryNotifications, setCurrentQueryNotifications] = useState([]);
  const [currentQueryPickupTimestamps, setCurrentQueryPickUpTimestamps] = useState([]);
  const [currentQueryMentor, setCurrentQueryMentor] = useState("");
  const [isQueryNotificationsModalOpen, setIsQueryNotificationsModalOpen] = useState(false);

  const csvLinkRef = useRef(null);
  const [queriesCSVData, setQueriesCSVData] = useState([]);

  const queryInfoContent = (
    <ul style={{ paddingLeft: 16 }}>
      <li>This page displays details of individual (unique) queries.</li>
      <li>If a query has been transferred, an info icon appears.</li>
      <li>Hovering over the icon shows: How many times it was transferred</li>
      <li>The most recent mentor name, who picked it</li>
    </ul>
  );

  const infoTransferredQuery = (info) => {
    return (
      <TooltipWrapper
        title={`This query is transferred ${info} time${info === 1 ? "" : "s"}`}
        body={<InfoCircleFilled />}
      />
    );
  };

  const fetchFilterFormRequisites = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/filter-form-requisites`, {})
      .then((res) => {
        const { batches, statuses, students, pageSize, mentors } = res.data;
        setBatchesList(batches);
        setQueryStatuses(statuses);
        setPageSize(pageSize);
        setStudentsList(students);
        setMentorsList(mentors);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchQueriesList = (page) => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/list`, {
        params: {
          filter,
          currentPage: page
        }
      })
      .then((res) => {
        const { queries, queriesCount } = res.data;
        setQueriesList(queries);
        setTotalQueries(queriesCount);
        showSuccessNotification("Queries fetched successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchNotificationsForQuery = (queryMongoId) => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/fetch-notifications`, {
        params: {
          queryMongoId
        }
      })
      .then((res) => {
        const { notifications, currentMentor } = res.data;
        setCurrentQueryNotifications(notifications);
        setCurrentQueryMentor(currentMentor);
        setIsQueryNotificationsModalOpen(true);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleQueriesDownloadCsv = () => {
    axios
      .get(`${ROOT_URL}/${BASE_BACKEND_URL}/csv-data`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const { queriesList } = res.data;
        setQueriesCSVData(queriesList);
        showSuccessNotification("Queries fetched successfully for download.");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchQueriesList(page);
  };

  useEffect(() => {
    fetchFilterFormRequisites();
  }, []);

  useEffect(() => {
    fetchQueriesList(currentPage || 1);
  }, [filter]);

  useEffect(() => {
    if (queriesCSVData?.length > 0 && csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  }, [queriesCSVData]);

  const orderByColumnNames = [
    { label: "Pickup Time (Mins) Asc", value: "pickupTimeMinsAsc" },
    { label: "Pickup Time (Mins) Desc", value: "pickupTimeMinsDesc" },
    { label: "Created At Asc", value: "createdAtAsc" },
    { label: "Created At Desc", value: "createdAtDesc" }
  ];
  const thankFilterOptions = [
    { label: "All", value: "all" },
    { label: "Auto Thanked", value: true },
    { label: "Manual Thanked", value: false }
  ];

  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index"
    },
    {
      title: "Title",
      key: "title",
      render: (row) => (
        <u>
          <a href={row.threadLink} target="_blank" rel="noreferrer">
            {row.title} {row.transferTimes > 0 && infoTransferredQuery(row.transferTimes)}
          </a>
        </u>
      )
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      sorter: (a, b) => {
        if (typeof a.level === "string" && typeof b.level === "string") {
          return a.level?.localeCompare(b.level) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Student",
      dataIndex: "studentName",
      key: "studentName",
      sorter: (a, b) => {
        if (typeof a.studentName === "string" && typeof b.studentName === "string") {
          return a.studentName?.localeCompare(b.studentName) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Status",
      key: "status",
      render: (row) => {
        // Define a mapping of status values to color and label
        const statusMap = {
          open: { color: "blue", label: "Open" },
          // eslint-disable-next-line camelcase
          in_progress: { color: "orange", label: "In Progress" },
          review: { color: "purple", label: "Under Review" },
          resolved: { color: "green", label: "Resolved" }
        };

        // Get the status object based on the row status or default to 'open'
        const statusObj = statusMap[row.status] || { color: "gray", label: "Unknown" };

        return (
          <Tag color={statusObj.color} key={statusObj.label}>
            {statusObj.label.toUpperCase()}
          </Tag>
        );
      },
      sorter: (a, b) => {
        if (typeof a.status === "string" && typeof b.status === "string") {
          return a.status?.localeCompare(b.status) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Pickup Time (Mins)",
      dataIndex: "pickupTimeMins",
      key: "pickupTimeMins"
    },
    {
      title: "Mentors",
      dataIndex: "mentors",
      key: "mentors"
    },
    {
      title: "First Picked At",
      dataIndex: "firstPickedAt",
      key: "firstPickedAt",
      render: (firstPickedAt) =>
        firstPickedAt ? dayjs(firstPickedAt).format("DD MMM YYYY | HH:mm:ss") : "--"
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (createdAt ? dayjs(createdAt).format("DD MMM YYYY | HH:mm:ss") : "--")
    },
    {
      title: "Actions",
      key: "action",
      render: (row) => (
        <div
          id="users_row_actions"
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around"
          }}
        >
          <Space wrap>
            <>
              {adminConfig?.discordAnalytics?.hasQueriesNotificationDataAccess && (
                <Button
                  type="primary"
                  onClick={() => {
                    fetchNotificationsForQuery(row.key);
                    setCurrentQueryPickUpTimestamps(row.pickUpTimestamps);
                  }}
                >
                  View Notifications
                </Button>
              )}
            </>
          </Space>
        </div>
      )
    }
  ];

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <Row gutter={[12, 12]} style={{ marginBottom: "10px" }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Batch:</span>
            <Select
              placeholder="Select Batch"
              defaultValue="All"
              style={{ width: "100%" }}
              onChange={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  batch: value
                }));
              }}
            >
              {(batchesList || []).map((item) => (
                <Select.Option value={item.value} key={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Date Range:</span>
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              value={
                filter.startDate && filter.endDate
                  ? [dayjs(filter.startDate), dayjs(filter.endDate)]
                  : []
              }
              onChange={(dates) => {
                if (dates) {
                  setFilter((prevFilter) => ({
                    ...prevFilter,
                    startDate: dates[0].clone().startOf("day").toDate(),
                    endDate: dates[1].clone().endOf("day").toDate()
                  }));
                } else {
                  setFilter((prevFilter) => ({
                    ...prevFilter,
                    startDate: null,
                    endDate: null
                  }));
                }
              }}
            />
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Query Status:</span>
            <Select
              placeholder="Select Status"
              defaultValue="All"
              style={{ width: "100%" }}
              allowClear
              onClear={() => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  status: undefined
                }));
              }}
              onChange={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  status: value
                }));
              }}
            >
              {(queryStatuses || []).map((item) => (
                <Select.Option value={item.value} key={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Order By:</span>
            <Select
              placeholder="Select Column"
              style={{ width: "100%" }}
              value={filter.orderByColumnName}
              onChange={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  orderByColumnName: value
                }));
              }}
            >
              {(orderByColumnNames || []).map((item) => (
                <Select.Option value={item.value} key={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Student:</span>
            <Select
              showSearch
              placeholder="Select Student"
              style={{ width: "100%" }}
              optionFilterProp="children"
              allowClear
              onClear={() => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  studentDiscordId: undefined
                }));
              }}
              onChange={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  studentDiscordId: value
                }));
              }}
              filterOption={(input, option) =>
                option?.children?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0
              }
            >
              {(studentsList || []).map((student) => (
                <Select.Option value={student.value} key={student.value}>
                  {student.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Mentor:</span>
            <Select
              showSearch
              placeholder="Select Mentor"
              style={{ width: "100%" }}
              optionFilterProp="children"
              allowClear
              onClear={() => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  mentorDiscordId: undefined
                }));
              }}
              onChange={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  mentorDiscordId: value
                }));
              }}
              filterOption={(input, option) =>
                option?.children?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0
              }
            >
              {(mentorsList || []).map((mentor) => (
                <Select.Option value={mentor.value} key={mentor.value}>
                  {mentor.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Thank Type:</span>
            <Select
              placeholder="Select Thank Type"
              style={{ width: "100%" }}
              value={filter.thankFilterOption}
              onChange={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  thankFilterOption: value
                }));
              }}
            >
              {(thankFilterOptions || []).map((item) => (
                <Select.Option value={item.value} key={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>

        <Col xs={12} sm={8} md={5} lg={4}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Exclude Internal:</span>
            <Switch
              checked={excludeInternal}
              onChange={(checked) => {
                setExcludeInternal(checked);
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  excludeInternal: checked
                }));
              }}
            />
          </div>
        </Col>

        <Col xs={12} sm={8} md={5} lg={4}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontWeight: "500" }}>Un-pinged thanks only:</span>
            <Switch
              checked={showUnPingedThankedMentorQueries}
              onChange={(checked) => {
                setShowUnPingedThankedMentorQueries(checked);
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  showUnPingedMentorThankedQueries: checked,
                  excludeInternal: !checked
                }));
                setExcludeInternal(!checked);
              }}
            />
          </div>
        </Col>

        <Col xs={24} sm={8} md={6} lg={4}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", height: "100%" }}>
            <span style={{ fontWeight: "500", visibility: "hidden" }}>Export:</span>
            <CSVLink
              asyncOnClick={true}
              data={queriesCSVData || []}
              ref={csvLinkRef}
              filename={"Discord Queries.csv"}
            />
            <Button
              type="primary"
              style={{ width: "100%" }}
              onClick={() => {
                handleQueriesDownloadCsv();
              }}
            >
              Download CSV
            </Button>
          </div>
        </Col>
      </Row>
      <div>
        <TooltipWrapper
          title={queryInfoContent}
          body={
            <Text strong>
              Query Info <InfoCircleFilled />
            </Text>
          }
        />
      </div>

      <Table
        columns={columns}
        dataSource={queriesList}
        bordered
        pagination={false}
        scroll={{ x: "max-content" }}
      />
      <br />
      <Pagination
        total={totalQueries}
        showSizeChanger={false}
        showQuickJumper={false}
        pageSize={pageSize}
        current={currentPage}
        onChange={handlePageChange}
      />
      {isQueryNotificationsModalOpen && (
        <QueryNotificationsTableModal
          notificationsList={currentQueryNotifications}
          currentMentor={currentQueryMentor}
          currentQueryPickupTimestamps={currentQueryPickupTimestamps}
          isModalOpen={isQueryNotificationsModalOpen}
          setIsModalOpen={setIsQueryNotificationsModalOpen}
        />
      )}
    </div>
  );
};

export default DiscordAnalyticsQueriesDashboard;
